import { create } from 'zustand';
import type { ChatMessage, MessageStatus } from '../types/chat';
import type { RecommendationData } from '../../../shared/services/aiService';
import type { Restaurant } from '../../search/types/restaurant';

interface ChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  currentStreamingMessageId: string | null;
  currentConversationId: string | null; // Track the current conversation thread ID in query_history

  // Actions
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void;
  appendToMessage: (id: string, text: string) => void;
  setStreaming: (isStreaming: boolean, messageId?: string | null) => void;
  clearMessages: () => void;
  getConversationHistory: () => Array<{ role: string; content: string }>;
  getAllMessages: () => ChatMessage[]; // Get all messages including restaurants
  // New methods for recommendations
  updateMessageRecommendations: (id: string, recommendation: RecommendationData) => void;
  setMessageLoadingRestaurants: (id: string, isLoading: boolean) => void;
  // 1.3.8: 修改签名，支持同时传递 restaurantReasons（与 restaurants 按索引对齐）
  updateMessageRestaurants: (id: string, restaurants: Restaurant[], restaurantReasons?: string[]) => void; // Append restaurants (supports multiple function calls)
  updateMessageWithRestaurants: (id: string, restaurants: Restaurant[], updates?: Partial<ChatMessage>) => void; // Update restaurants and message content atomically
  // 1.3.9: 新增方法，用于替换整个 restaurantReasons 数组（而非追加）
  replaceMessageRestaurantReasons: (id: string, restaurantReasons: string[]) => void;
  // Load conversation from history
  loadConversationFromHistory: (conversationMessages: ChatMessage[]) => void;
  // Conversation thread management
  setCurrentConversationId: (id: string | null) => void;
  getCurrentConversationId: () => string | null;
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [],
  isStreaming: false,
  currentStreamingMessageId: null,
  currentConversationId: null, // Track current conversation thread ID

  addMessage: (message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now(),
    };
    set((state) => ({
      messages: [...state.messages, newMessage],
    }));
    return newMessage.id;
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    }));
  },

  appendToMessage: (id, text) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, content: msg.content + text } : msg
      ),
    }));
  },

  setStreaming: (isStreaming, messageId = null) => {
    set({
      isStreaming,
      currentStreamingMessageId: messageId,
    });
  },

  clearMessages: () => {
    set({
      messages: [],
      isStreaming: false,
      currentStreamingMessageId: null,
      currentConversationId: null, // Reset conversation ID when clearing messages
    });
  },

  getConversationHistory: () => {
    const { messages } = get();
    return messages
      .filter((msg) => msg.status !== 'error')
      .map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));
  },

  getAllMessages: () => {
    const { messages } = get();
    return messages;
  },

  updateMessageRecommendations: (id, recommendation) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id
          ? {
            ...msg,
            recommendationData: recommendation,
            recommendedTypes: recommendation.recommendedTypes,
            // Note: showRecommendButton is no longer needed since restaurants are displayed automatically
            showRecommendButton: false,
          }
          : msg
      ),
    }));
  },

  setMessageLoadingRestaurants: (id, isLoading) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, isLoadingRestaurants: isLoading } : msg
      ),
    }));
  },

  // 1.3.8: 修改实现，支持同时更新 restaurantReasons
  updateMessageRestaurants: (id, restaurants, restaurantReasons) => {
    set((state) => ({
      messages: state.messages.map((msg) => {
        if (msg.id === id) {
          // Append restaurants to existing restaurants array (support multiple function calls)
          const existingRestaurants = msg.restaurants || [];
          const existingReasons = msg.restaurantReasons || [];

          // 记录新增餐厅的索引
          const newRestaurantsWithIndices: { restaurant: Restaurant; originalIndex: number }[] = [];
          restaurants.forEach((r, idx) => {
            if (!existingRestaurants.find(existing => existing.id === r.id)) {
              newRestaurantsWithIndices.push({ restaurant: r, originalIndex: idx });
            }
          });

          // 提取新增餐厅
          const newRestaurants = newRestaurantsWithIndices.map(item => item.restaurant);
          const allRestaurants = [...existingRestaurants, ...newRestaurants];

          // 1.3.8: 同时追加对应的 reasons（保持索引对齐）
          let allReasons = existingReasons;
          if (restaurantReasons && restaurantReasons.length > 0) {
            // 只追加新增餐厅对应的 reasons
            const newReasons = newRestaurantsWithIndices.map(item =>
              restaurantReasons[item.originalIndex] || ''
            );
            allReasons = [...existingReasons, ...newReasons];
          }

          return {
            ...msg,
            restaurants: allRestaurants,
            restaurantReasons: allReasons.length > 0 ? allReasons : undefined,
            isLoadingRestaurants: false,
            showRecommendButton: false, // No button needed - restaurants are displayed automatically
          };
        }
        return msg;
      }),
    }));
  },

  updateMessageWithRestaurants: (id, restaurants, updates = {}) => {
    set((state) => ({
      messages: state.messages.map((msg) => {
        if (msg.id === id) {
          // Append restaurants to existing restaurants array (support multiple function calls)
          const existingRestaurants = msg.restaurants || [];
          const newRestaurants = restaurants.filter(
            r => !existingRestaurants.find(existing => existing.id === r.id)
          );
          const allRestaurants = [...existingRestaurants, ...newRestaurants];

          return {
            ...msg,
            restaurants: allRestaurants,
            isLoadingRestaurants: false,
            showRecommendButton: false, // No button needed - restaurants are displayed automatically
            ...updates, // Apply any additional updates (content, status, etc.)
          };
        }
        return msg;
      }),
    }));
  },

  // 1.3.9: 新增方法，用于替换整个 restaurantReasons 数组（而非追加）
  // 当收到后端的 updated:true 标志时使用此方法
  replaceMessageRestaurantReasons: (id, restaurantReasons) => {
    console.log('🔄 [chatStore] replaceMessageRestaurantReasons called:', {
      messageId: id,
      newReasonsCount: restaurantReasons.length,
    });

    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id
          ? {
            ...msg,
            restaurantReasons: restaurantReasons.length > 0 ? restaurantReasons : undefined,
            lastUpdate: Date.now(), // 1.3.9: 强制React重渲染
          }
          : msg
      ),
    }));

    console.log('✅ [chatStore] After update');
  },

  loadConversationFromHistory: (conversationMessages) => {
    // Clear current messages and restore from history
    // Ensure each message has proper structure
    const restoredMessages = conversationMessages.map((msg) => ({
      ...msg,
      // Ensure status is set to completed for restored messages
      status: (msg.status || 'completed') as MessageStatus,
      // Ensure timestamp is preserved or set to current time
      timestamp: msg.timestamp || Date.now(),
    }));

    set({
      messages: restoredMessages,
      isStreaming: false,
      currentStreamingMessageId: null,
      // Note: currentConversationId should be set by the caller if loading from history
    });
  },

  setCurrentConversationId: (id) => {
    set({ currentConversationId: id });
  },

  getCurrentConversationId: () => {
    return get().currentConversationId;
  },
}));

